import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";
import { analytics, EVENTS } from "../analytics";
import { useInterestRegistration } from "../hooks/useInterestRegistration";

const STORAGE_KEY = "beme_interest_modal_seen";
const SHOW_DELAY_MS = 3000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  sourcePage: "landing" | "service";
}

const InterestCaptureModal: React.FC<Props> = ({ sourcePage }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const { status, register } = useInterestRegistration();

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(() => {
      setOpen(true);
      analytics.track(EVENTS.VIEW_INTEREST_MODAL, { from: sourcePage });
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeAndRemember = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setOpen(false);
  };

  const handleClose = () => {
    if (status !== "success" && status !== "duplicate") {
      analytics.track(EVENTS.DISMISS_INTEREST_MODAL, { from: sourcePage });
    }
    closeAndRemember();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email) || status === "submitting") return;

    const result = await register(email, { source_page: sourcePage });
    if (result === "error") return;

    analytics.track(EVENTS.SUBMIT_INTEREST_MODAL, { from: sourcePage });
    localStorage.setItem(STORAGE_KEY, "true");
  };

  const isDone = status === "success" || status === "duplicate";

  return (
    <Modal open={open} onClose={handleClose}>
      {isDone ? (
        <div className="text-center">
          <h3 className="text-xl font-bold text-main mb-3">
            {t("interestCaptureModal.successTitle")}
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            {status === "duplicate"
              ? t("interestCaptureModal.alreadyRegistered")
              : t("interestCaptureModal.successBody")}
          </p>
          <button
            onClick={closeAndRemember}
            className="w-full bg-main text-white font-bold px-6 py-4 rounded-xl hover:opacity-90 transition-colors"
          >
            {t("interestCaptureModal.close")}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <h3 className="text-xl font-bold text-main mb-3">
            {t("interestCaptureModal.title")}
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            {t("interestCaptureModal.body")}
          </p>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("interestCaptureModal.placeholder") as string}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-main"
          />
          {status === "error" && (
            <p className="text-red-500 text-xs mb-3">{t("interestCaptureModal.error")}</p>
          )}
          <button
            type="submit"
            disabled={status === "submitting"}
            className="w-full bg-main text-white font-bold px-6 py-4 rounded-xl hover:opacity-90 transition-colors disabled:opacity-60"
          >
            {status === "submitting"
              ? t("interestCaptureModal.submitting")
              : t("interestCaptureModal.cta")}
          </button>
        </form>
      )}
    </Modal>
  );
};

export default InterestCaptureModal;
